import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Mail, CalendarClock } from "lucide-react";
import { Input, Toggle } from "./fields";
import { getEmailSettings, putEmailSettings, getRecapSettings, putRecapSettings } from "@/lib/api";

export const AdminSettings = () => {
  const [data, setData] = useState(null);
  const [recap, setRecap] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    getEmailSettings().then(setData).catch(() => toast.error("Impossible de charger les paramètres."));
    getRecapSettings().then(setRecap).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      setData(await putEmailSettings(data));
      if (recap) setRecap(await putRecapSettings({ hour: recap.hour, minute: recap.minute, email: recap.email, enabled: recap.enabled }));
      toast.success("Paramètres enregistrés.");
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-electric" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2 flex items-start gap-3">
        <Mail size={18} className="text-electric mt-0.5 shrink-0" />
        <span>Configurez vos e-mails sans toucher au code. L'adresse de <strong className="text-white">notification</strong> reçoit les nouvelles demandes ; l'adresse de <strong className="text-white">réponse</strong> est utilisée comme « Répondre à » quand vous répondez depuis la boîte de réception.</span>
      </div>

      <Input label="Adresse e-mail de notification (reçoit les demandes du site)" value={data.notifyEmail} onChange={(v) => set("notifyEmail", v)} />
      <Input label="Adresse « Répondre à » (pour vos réponses)" value={data.replyFromEmail} onChange={(v) => set("replyFromEmail", v)} />
      <Input label="Nom de l'expéditeur (affiché dans les e-mails)" value={data.fromName} onChange={(v) => set("fromName", v)} />

      {recap && (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-4" data-testid="recap-settings">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><CalendarClock size={14} /> Récapitulatif des participants (jour J)</span>
          <p className="text-xs text-secondary2">Chaque jour de formation, un e-mail récapitulatif (participants confirmés, places disponibles, lien feuille de présence) est envoyé automatiquement à l'adresse ci-dessous.</p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Heure d'envoi (Paris)" type="number" value={recap.hour} onChange={(v) => setRecap((r) => ({ ...r, hour: Number(v) }))} />
            <Input label="Minute" type="number" value={recap.minute} onChange={(v) => setRecap((r) => ({ ...r, minute: Number(v) }))} />
          </div>
          <Input label="Adresse e-mail de réception du récapitulatif" value={recap.email} onChange={(v) => setRecap((r) => ({ ...r, email: v }))} />
          <Toggle label="Envoi automatique activé" checked={!!recap.enabled} onChange={(v) => setRecap((r) => ({ ...r, enabled: v }))} testid="recap-enabled" />
          {(recap.history || []).length > 0 && (
            <div className="text-xs text-secondary2 space-y-1">
              <div className="text-[10px] uppercase tracking-wider">Derniers envois</div>
              {recap.history.slice(0, 5).map((h, i) => (
                <div key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} · {h.day} · {h.confirmed} confirmé(s) · {h.sent ? "✓ envoyé" : "✗ échec"} ({h.trigger})</div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
      </button>
    </div>
  );
};
